import React, { useState } from 'react';
import { Home, Mail, User, Phone, CheckCircle2 } from 'lucide-react';
import { useLeadStore } from '../store/useLeadStore';
export function LeadForm() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success'>('idle');
  const addLead = useLeadStore(state => state.addLead);
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('submitting');
    const formData = new FormData(e.currentTarget);
    const address = formData.get('address') as string;
    const name = formData.get('name') as string;
    const phone = formData.get('phone') as string;
    const email = formData.get('email') as string;
    // Simulate API call
    setTimeout(() => {
      addLead({
        address,
        name,
        phone,
        email
      });
      setFormState('success');
    }, 1500);
  };
  return <section id="lead-form" className="py-20 bg-gray-50" data-id="element-212">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-213">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden flex flex-col lg:flex-row border border-gray-100" data-id="element-214">
          {/* Left side - Value Prop */}
          <div className="lg:w-5/12 bg-gray-900 p-10 lg:p-12 text-white flex flex-col justify-center relative overflow-hidden" data-id="element-215">
            <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-brand-gold rounded-full opacity-20 blur-3xl" data-id="element-216"></div>
            <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-40 h-40 bg-brand-gold rounded-full opacity-20 blur-3xl" data-id="element-217"></div>

            <div className="relative z-10" data-id="element-218">
              <div className="inline-flex items-center space-x-2 bg-brand-gold/20 text-brand-gold px-3 py-1 rounded-full mb-6" data-id="element-219">
                <span className="w-2 h-2 rounded-full bg-brand-gold animate-pulse" data-id="element-220"></span>
                <span className="text-xs font-bold tracking-widest uppercase" data-id="element-221">
                  Free Video Audit
                </span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-serif mb-4" data-id="element-222">
                Get Your Custom 3-Minute Equity & Upgrade Audit
              </h2>
              <p className="text-gray-300 mb-8 font-light leading-relaxed" data-id="element-223">
                Automated PDFs don't tell the whole story. Submit your property
                below, and Kareem will personally record a custom screen-share
                video analyzing your home's current value, renovation ROI
                potential, and ADU zoning possibilities.
              </p>

              <ul className="space-y-4" data-id="element-224">
                {['Identify high-ROI renovation opportunities', 'ADU potential and zoning analysis', 'Tax implications of improvements', '100% free, no obligation to list'].map((item, i) => <li key={i} className="flex items-start" data-id="element-225">
                    <CheckCircle2 className="w-5 h-5 text-brand-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-226" />
                    <span className="text-sm text-gray-200" data-id="element-227">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          {/* Right side - Form */}
          <div className="lg:w-7/12 p-10 lg:p-12" data-id="element-228">
            {formState === 'success' ? <div className="h-full flex flex-col items-center justify-center text-center space-y-4 py-10" data-id="element-229">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4" data-id="element-230">
                  <CheckCircle2 className="w-8 h-8" data-id="element-231" />
                </div>
                <h3 className="text-2xl font-serif text-gray-900" data-id="element-232">
                  Request Received
                </h3>
                <p className="text-gray-600 max-w-md" data-id="element-233">
                  Thank you! Kareem will review your property details and send
                  your custom video audit shortly.
                </p>
                <button onClick={() => setFormState('idle')} className="mt-6 text-brand-gold font-medium hover:underline" data-id="element-234">
                  Submit another property
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-235">
                <div data-id="element-236">
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-237">
                    Property Address in 91311
                  </label>
                  <div className="relative" data-id="element-238">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-239">
                      <Home className="h-5 w-5 text-gray-400" data-id="element-240" />
                    </div>
                    <input type="text" id="address" name="address" required aria-required="true" className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-gold focus:border-brand-gold sm:text-sm transition-all outline-none" placeholder="e.g., 1234 Topanga Canyon Blvd, Chatsworth" data-id="element-241" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-242">
                  <div data-id="element-243">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-244">
                      Full Name
                    </label>
                    <div className="relative" data-id="element-245">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-246">
                        <User className="h-5 w-5 text-gray-400" data-id="element-247" />
                      </div>
                      <input type="text" id="name" name="name" required aria-required="true" className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-gold focus:border-brand-gold sm:text-sm transition-all outline-none" placeholder="John Doe" data-id="element-248" />
                    </div>
                  </div>
                  <div data-id="element-249">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-250">
                      Phone Number
                    </label>
                    <div className="relative" data-id="element-251">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-252">
                        <Phone className="h-5 w-5 text-gray-400" data-id="element-253" />
                      </div>
                      <input type="tel" id="phone" name="phone" required aria-required="true" className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-gold focus:border-brand-gold sm:text-sm transition-all outline-none" placeholder="(818) 555-0123" data-id="element-254" />
                    </div>
                  </div>
                </div>

                <div data-id="element-255">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-256">
                    Email Address
                  </label>
                  <div className="relative" data-id="element-257">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-258">
                      <Mail className="h-5 w-5 text-gray-400" data-id="element-259" />
                    </div>
                    <input type="email" id="email" name="email" required aria-required="true" className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-gold focus:border-brand-gold sm:text-sm transition-all outline-none" placeholder="john@example.com" data-id="element-260" />
                  </div>
                </div>

                <div className="pt-2" data-id="element-261">
                  <button type="submit" disabled={formState === 'submitting'} className="w-full flex justify-center py-4 px-4 border border-transparent rounded-xl shadow-md text-sm font-bold uppercase tracking-wide text-white bg-brand-gold hover:bg-[#B0885B] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-gold transition-all disabled:opacity-70 active:scale-[0.98]" data-id="element-262">
                    {formState === 'submitting' ? 'Processing...' : 'Request My Video Audit'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 text-center mt-4" data-id="element-263">
                  By submitting this form, you agree to be contacted by Kareem
                  Jamal regarding your real estate strategy. Your information is
                  kept strictly confidential.
                </p>
              </form>}
          </div>
        </div>
      </div>
    </section>;
}